import { MAX_LEVELS } from '@/feature/order_book/constants.ts';
import type { FillPrognosis, PriceLevel } from '@/feature/order_book/types.ts';
import { createPriceLevel, roundAskToTickSize, roundBidToTickSize } from './helpers.ts';
import type { NormalizedPriceLevel } from './types.ts';

export type { FillPrognosis };

// FP epsilon: levels/deltas with an asset size below this are treated as numerically zero.
const MIN_TOTAL_SIZE = 0.000_000_01;

// UX threshold: levels below this USD notional are hidden from the rendered book (they'd round
// to "0" in USD mode). The best bucket per side is exempt — the spread is measured against the
// raw best order, so hiding its row would anchor the spread to an invisible price.
const MIN_DISPLAY_NOTIONAL_USD = 1;

export class LevelAggregator {
  private rawOrders = new Map<string, NormalizedPriceLevel>();
  private levelMap = new Map<number, { totalSize: number; version: number }>();
  // Monotonic per-aggregator counter; bumped only when an incremental update mutates or inserts a bucket.
  // Snapshots and full rebuilds (tick-size change) set buckets to version 0, which the UI reads as "no flash".
  private versionCounter = 0;

  private tickSizeNum: number;
  private readonly quoteDecimals: number;
  private readonly assetDecimals: number;
  private readonly isBidSide: boolean;
  // Derived caches; null means stale. All are cleared together by invalidate() on every mutation.
  private cachedResult: PriceLevel[] | null = null;
  private sortedPrices: number[] | null = null;
  // Execution view for the fill prognosis: prefix sums over RAW order prices, not display buckets
  // (buckets sit up to a tick off mid, and real fills sweep the sub-$1 levels getLevels() hides).
  private cumulative: { prices: number[]; sizes: number[]; quotes: number[] } | null = null;

  constructor(tickSize: string, quoteDecimals: number, assetDecimals: number, isBidSide: boolean = true) {
    this.tickSizeNum = parseFloat(tickSize);
    this.quoteDecimals = quoteDecimals;
    this.assetDecimals = assetDecimals;
    this.isBidSide = isBidSide;
  }

  private rebuildAggregatedLevels() {
    this.levelMap.clear();
    for (const order of this.rawOrders.values()) {
      const bucketPrice = this.roundPrice(order.price);
      const existing = this.levelMap.get(bucketPrice);
      const newTotalSize = (existing ? existing.totalSize : 0) + order.size;

      if (newTotalSize > MIN_TOTAL_SIZE) {
        if (existing) {
          existing.totalSize = newTotalSize;
        } else {
          this.levelMap.set(bucketPrice, { totalSize: newTotalSize, version: 0 });
        }
      }
    }
    this.invalidate();
  }

  private updateAggregatedLevel(priceNum: number, sizeDelta: number) {
    const bucketPrice = this.roundPrice(priceNum);
    const level = this.levelMap.get(bucketPrice);
    const currentSize = level ? level.totalSize : 0;
    const newSize = currentSize + sizeDelta;

    if (newSize > MIN_TOTAL_SIZE) {
      const version = ++this.versionCounter;
      if (level) {
        level.totalSize = newSize;
        level.version = version;
      } else {
        this.levelMap.set(bucketPrice, { totalSize: newSize, version });
      }
    } else {
      this.levelMap.delete(bucketPrice);
    }
    this.invalidate();
  }

  buildFromSnapshot(orders: NormalizedPriceLevel[]) {
    this.rawOrders.clear();
    this.levelMap.clear();

    for (const pl of orders) {
      if (pl.count > 0) {
        this.rawOrders.set(pl.priceStr, pl);
      }
    }
    this.rebuildAggregatedLevels();
  }

  updateLevels(updates: NormalizedPriceLevel[]) {
    for (const pl of updates) {
      const existingRawOrder = this.rawOrders.get(pl.priceStr);

      if (pl.count === 0) {
        if (existingRawOrder) {
          this.updateAggregatedLevel(existingRawOrder.price, -existingRawOrder.size);
          this.rawOrders.delete(pl.priceStr);
        }
      } else {
        const sizeDelta = pl.size - (existingRawOrder ? existingRawOrder.size : 0);

        if (sizeDelta >= MIN_TOTAL_SIZE || sizeDelta <= -MIN_TOTAL_SIZE) {
          this.updateAggregatedLevel(pl.price, sizeDelta);
        }

        this.rawOrders.set(pl.priceStr, pl);
      }
    }
  }

  setTickSize(tickSize: string) {
    const newTickSizeNum = parseFloat(tickSize);
    if (newTickSizeNum !== this.tickSizeNum) {
      this.tickSizeNum = newTickSizeNum;
      this.rebuildAggregatedLevels();
    }
  }

  invalidate() {
    this.cachedResult = null;
    this.sortedPrices = null;
    this.cumulative = null;
  }

  private roundPrice(price: number): number {
    return this.isBidSide ? roundBidToTickSize(price, this.tickSizeNum) : roundAskToTickSize(price, this.tickSizeNum);
  }

  // Best-first ordering for this side (bids descending, asks ascending).
  private bestFirst(a: number, b: number): number {
    return this.isBidSide ? b - a : a - b;
  }

  // How many leading prices of a best-first sorted array are still at-or-better than `bound`
  // for this side (asks: price <= bound, bids: price >= bound).
  private countAtOrBetter(sortedPrices: number[], bound: number): number {
    let lo = 0;
    let hi = sortedPrices.length;
    while (lo < hi) {
      const mid = (lo + hi) >>> 1;
      if (this.isBidSide ? sortedPrices[mid] >= bound : sortedPrices[mid] <= bound) {
        lo = mid + 1;
      } else {
        hi = mid;
      }
    }
    return lo;
  }

  private ensureSortedPrices(): number[] {
    if (!this.sortedPrices) {
      const prices: number[] = [];
      let best: number | undefined;
      let bestVisible = false;
      for (const [price, data] of this.levelMap) {
        if (data.totalSize <= MIN_TOTAL_SIZE) continue;
        const visible = price * data.totalSize >= MIN_DISPLAY_NOTIONAL_USD;
        if (visible) prices.push(price);
        if (best === undefined || this.bestFirst(price, best) < 0) {
          best = price;
          bestVisible = visible;
        }
      }
      // Tick rounding is monotone, so the raw best order always lands in the best bucket —
      // keeping it visible even as dust keeps the spread anchored to a rendered row.
      if (best !== undefined && !bestVisible) {
        prices.push(best);
      }
      this.sortedPrices = prices.sort((a, b) => this.bestFirst(a, b));
    }
    return this.sortedPrices;
  }

  getLevels(limit: number = MAX_LEVELS): PriceLevel[] {
    if (this.cachedResult) {
      return this.cachedResult;
    }

    const topPrices = this.ensureSortedPrices().slice(0, limit);

    // One positional pass folds cumulative sums into each row. Consumers (bar widths, total
    // column) read these directly instead of re-reducing per render.
    let cumulativeSizeNum = 0;
    let cumulativeTotalNum = 0;
    this.cachedResult = topPrices.map((bucketPrice) => {
      const data = this.levelMap.get(bucketPrice)!;
      const level = createPriceLevel(
        bucketPrice,
        data.totalSize,
        this.tickSizeNum < 1 ? this.quoteDecimals : 0,
        this.assetDecimals,
        data.version
      );
      cumulativeSizeNum += level.sizeNum;
      cumulativeTotalNum += level.totalNum;
      level.cumulativeSizeNum = cumulativeSizeNum;
      level.cumulativeTotalNum = cumulativeTotalNum;
      return level;
    });

    return this.cachedResult;
  }

  private ensureCumulative(): { prices: number[]; sizes: number[]; quotes: number[] } {
    if (this.cumulative) {
      return this.cumulative;
    }
    // Duplicate adjacent prices (distinct feed strings, same number) are harmless — every
    // quantity derived from the prefix sums is invariant under that split.
    const levels: NormalizedPriceLevel[] = [];
    for (const order of this.rawOrders.values()) {
      if (order.size > MIN_TOTAL_SIZE) levels.push(order);
    }
    levels.sort((a, b) => this.bestFirst(a.price, b.price));

    const prices = new Array<number>(levels.length);
    const sizes = new Array<number>(levels.length);
    const quotes = new Array<number>(levels.length);
    let sizeAcc = 0;
    let quoteAcc = 0;
    for (let i = 0; i < levels.length; i++) {
      const { price, size } = levels[i];
      prices[i] = price;
      sizeAcc += size;
      quoteAcc += price * size;
      sizes[i] = sizeAcc;
      quotes[i] = quoteAcc;
    }
    this.cumulative = { prices, sizes, quotes };
    return this.cumulative;
  }

  // Rendered rows a fill reaching `worstRawPrice` touches, so the count matches the book UI.
  // Clamped to 1: a fill can end inside dust-hidden rows.
  private displayRowsThrough(worstRawPrice: number): number {
    return Math.max(1, this.countAtOrBetter(this.ensureSortedPrices(), this.roundPrice(worstRawPrice)));
  }

  // O(log N) against prefix sums rebuilt lazily per book mutation — safe per-keystroke.
  //
  //  - `referencePrice`: baseline for priceImpactPct, typically mid. Falls back to this side's
  //    best (keeps the method standalone-testable). depthImpactPct always uses best.
  //  - `limitPrice`: the exchange's price cap, best * (1 ± slip%). MUST derive from best, not
  //    from `referencePrice` — the backend's slippage filter is best-based.
  getFillPrognosis(assetSize: number, referencePrice?: number, limitPrice?: number): FillPrognosis | undefined {
    if (!Number.isFinite(assetSize) || assetSize <= 0) {
      return undefined;
    }
    const { prices, sizes, quotes } = this.ensureCumulative();
    if (sizes.length === 0) {
      return undefined;
    }

    const ref = referencePrice && referencePrice > 0 ? referencePrice : prices[0];
    const slippageLimitedSize = limitPrice && limitPrice > 0 ? this.maxSizeWithinLimit(limitPrice, prices, sizes) : undefined;
    const visibleDepth = sizes[sizes.length - 1];
    const fullyFilled = assetSize <= visibleDepth;

    // Price only what actually executes: the request clipped by book depth AND by the slippage
    // cap. Pricing the un-clipped size would quote a fill that walks levels the exchange
    // rejects — the panel would then show "Partial fill (~90%)" beside a 100% average.
    const fillSize = Math.min(assetSize, visibleDepth, slippageLimitedSize ?? Number.POSITIVE_INFINITY);
    const endIdx = fillSize > 0 ? this.firstIndexCovering(fillSize, sizes) : 0;
    const beforeSize = endIdx > 0 ? sizes[endIdx - 1] : 0;
    const beforeQuote = endIdx > 0 ? quotes[endIdx - 1] : 0;
    // fillSize === 0 (cap below best) has no fill to average — quote best rather than NaN.
    const avgPrice = fillSize > 0 ? (beforeQuote + (fillSize - beforeSize) * prices[endIdx]) / fillSize : prices[0];
    // Total splits into depth (avg vs best) + spread (best vs mid). Both zero-floored; when no
    // reference was passed, ref IS best and the spread component is 0 by construction.
    const best = prices[0];
    return {
      levels: this.displayRowsThrough(prices[endIdx]),
      avgPrice,
      priceImpactPct: (Math.abs(avgPrice - ref) / ref) * 100,
      depthImpactPct: (Math.abs(avgPrice - best) / best) * 100,
      spreadImpactPct: (Math.abs(best - ref) / ref) * 100,
      fullyFilled,
      visibleDepth,
      slippageLimitedSize,
    };
  }

  // First level whose cumulative size covers `size`. Callers pass a size already clipped to
  // total depth, so a covering level always exists.
  private firstIndexCovering(size: number, sizes: number[]): number {
    let lo = 0;
    let hi = sizes.length - 1;
    while (lo < hi) {
      const mid = (lo + hi) >>> 1;
      if (sizes[mid] >= size) {
        hi = mid;
      } else {
        lo = mid + 1;
      }
    }
    return lo;
  }

  // Cumulative asset size up to the last level still acceptable under `limitPrice`
  // (asks: price ≤ limit, bids: price ≥ limit) — 0 when no level fits.
  private maxSizeWithinLimit(limitPrice: number, prices: number[], sizes: number[]): number {
    const count = this.countAtOrBetter(prices, limitPrice);
    return count === 0 ? 0 : sizes[count - 1];
  }

  getBestPrice(): number | undefined {
    if (this.rawOrders.size === 0) {
      return undefined;
    }

    const iterator = this.rawOrders.values();
    let bestPrice = iterator.next().value!.price;
    const bestFunc = this.isBidSide ? Math.max : Math.min;

    for (const order of iterator) {
      bestPrice = bestFunc(bestPrice, order.price);
    }

    return bestPrice;
  }
}
